import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import Footer from "../components/Footer";
import useAuthStore from "../store/authStore";
import PublicNav from "../components/PublicNav";

export default function ConnectLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const employerToken = useAuthStore((s) => s.employerToken);

  const dashboardWithToken = `/employers/dashboard?connect=${token}`;
  const encodedNext = encodeURIComponent(dashboardWithToken);

  useEffect(() => {
    document.title = "Joborigo: Connect with a candidate";
    // Save token to sessionStorage so it survives the auth redirect chain
    if (token) sessionStorage.setItem("joborigo_connect_token", token);
  }, [token]);

  // If already logged in as employer — redirect straight to dashboard with token
  useEffect(() => {
    if (employerToken && token) {
      navigate(dashboardWithToken, { replace: true });
    }
  }, [employerToken, token]);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <PublicNav />

      <main className="flex-1 max-w-2xl mx-auto px-6 py-16 w-full">
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-brand-violet flex items-center justify-center text-white text-3xl mx-auto mb-6 shadow-lg shadow-brand-violet/20">
            🔗
          </div>
          <h1 className="text-3xl font-extrabold mb-3 leading-tight">
            A candidate wants to hear from you.
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed max-w-lg mx-auto">
            This link was shared by a job seeker using{" "}
            <span className="font-semibold text-brand-violet dark:text-brand-violet-on-dark">Joborigo</span> —
            a platform that tracks job applications and connects candidates with
            employers in real time.
          </p>
        </div>

        <div className="bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-2xl p-6 mb-8">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
            What this link does
          </h2>
          <div className="flex flex-col gap-4">
            {[
              {
                icon: "✅",
                title: "Confirms genuine interest",
                desc: "This candidate applied for a role at your company and proactively shared this link, a clear signal of real interest.",
              },
              {
                icon: "🔄",
                title: "Enables real-time status updates",
                desc: "Once connected, you can push status updates directly to this candidate's Joborigo dashboard: Interview scheduled, Offer sent, Rejected, instantly and without emails.",
              },
              {
                icon: "👻",
                title: "Eliminates ghosting",
                desc: "Candidates on Joborigo track employer responsiveness. Connecting means you become a company that communicates, a stronger employer brand at zero extra effort.",
              },
              {
                icon: "🔒",
                title: "Privacy-first",
                desc: "This token does not expose the candidate's email address. It is a one-time-use link that expires in 72 hours.",
              },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3">
                <span className="text-lg flex-shrink-0 mt-0.5">
                  {item.icon}
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-0.5">
                    {item.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
            How to connect
          </h2>
          <div className="flex flex-col gap-3">
            {[
              {
                step: "1",
                title: "Create a free employer account",
                desc: "Register at joborigo.com. Free, no credit card required. Takes under 2 minutes.",
              },
              {
                step: "2",
                title: "Connect the candidate from your dashboard",
                desc: "Once registered or signed in, you land on your dashboard where the token is pre-filled. One click connects the candidate, no API needed.",
              },
              {
                step: "3",
                title: "Generate an API key (optional)",
                desc: "To push automated status updates from your ATS, generate an API key from your dashboard. See the API documentation for details.",
              },
              {
                step: "4",
                title: "Push status updates",
                desc: "Call POST /employer/status-push whenever this candidate's status changes. They see it immediately on their Joborigo dashboard.",
              },
            ].map((item) => (
              <div
                key={item.step}
                className="flex gap-4 p-4 bg-gray-50 dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10"
              >
                <div className="w-7 h-7 rounded-full bg-brand-violet text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {item.step}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-0.5">
                    {item.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-8 bg-gray-50 dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10 p-4">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
            Candidate connect token
          </p>
          <code className="text-sm font-mono text-brand-violet dark:text-brand-violet-on-dark break-all">
            {token}
          </code>
          <p className="text-xs text-gray-400 mt-2">
            This token is pre-filled in your dashboard after you register or
            sign in.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <Link
            to={`/employers/register?connect=${token}`}
            className="flex-1 bg-brand-violet text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition text-center"
          >
            Create free employer account
          </Link>
          <Link
            to={`/employers/login?next=${encodedNext}`}
            className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition text-center"
          >
            Sign in to existing account
          </Link>
        </div>
        <div className="text-center">
          <Link
            to="/employers/api"
            className="text-sm text-brand-violet dark:text-brand-violet-on-dark hover:underline"
          >
            View full API documentation →
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
